import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { STUDENT_INVITE_QUERY_PARAM, type ManualInviteAccountFor } from '@court-sync/shared';
import { academyService } from '@/services/academy';
import { publicOrgService } from '@/services/publicOrg';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import {
  formatManualInviteStudentName,
  useManualInvitePreview,
} from '@/hooks/useManualInvitePreview';
import type { Academy } from '@court-sync/shared';
import type { PublicOrganizationProfile } from '@/services/publicOrg';
import { AcademyBrandingProvider } from '@/contexts/AcademyBrandingContext';
import { AcademyGuestRegistrationBlock } from './publicBooking/AcademyGuestRegistrationBlock';
import { userFormSchema, type UserFormData } from './publicBooking/schemas';
import { buildOrgLoginPath, buildOrgEmailVerificationPath } from '@/lib/orgPaths';
import { submitAcademyRegister } from './academyAuthHandlers';
import { submitClubRegister } from './clubAuthHandlers';
import { ManualInviteAccountTypeSelector } from '@/components/students/ManualInviteAccountTypeSelector';
import { isStarterMemberAuthBlockedAcademy } from '@/utils/publicBrowseMode';
import { StarterMemberAuthNotFound } from '@/components/public/StarterMemberAuthNotFound';

export default function OrgRegisterPage() {
  const orgSlug = useOrgSlug();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orgProfile, setOrgProfile] = useState<PublicOrganizationProfile | null>(null);
  const [academy, setAcademy] = useState<Academy | null>(null);
  const { p } = useOrgPaths(orgProfile ? { slug: orgProfile.organization.slug } : null);
  const [loadingOrg, setLoadingOrg] = useState(() => Boolean(orgSlug));
  const [orgError, setOrgError] = useState<string | null>(null);
  const [registerSubmitting, setRegisterSubmitting] = useState(false);
  const [manualInviteAccountFor, setManualInviteAccountFor] = useState<ManualInviteAccountFor | null>(null);

  const returnUrl = searchParams.get('returnUrl');
  const manualInviteToken = searchParams.get(STUDENT_INVITE_QUERY_PARAM)?.trim() || null;
  const { status: invitePreviewStatus, preview: invitePreview } = useManualInvitePreview(manualInviteToken);
  const dashboardReturnPath = p ? (returnUrl || p('/dashboard')) : '/dashboard';

  const registerForm = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phone: '',
      termsAccepted: false,
      privacyAccepted: false,
      marketingEmailConsent: false,
    },
  });

  useEffect(() => {
    if (!orgSlug) return;
    publicOrgService
      .getProfile(orgSlug)
      .then(async (res) => {
        if (!res.success || !res.data) {
          setOrgProfile(null);
          setOrgError(res.error || 'Organization not found');
          return;
        }
        setOrgProfile(res.data);
        setOrgError(null);
        if (res.data.academySlug) {
          try {
            const acad = await academyService.getAcademyBySlug(res.data.academySlug);
            setAcademy(acad);
          } catch {
            setAcademy(null);
          }
        }
      })
      .catch(() => {
        setOrgProfile(null);
        setOrgError('Organization not found');
      })
      .finally(() => setLoadingOrg(false));
  }, [orgSlug]);

  useEffect(() => {
    if (invitePreviewStatus !== 'valid' || !invitePreview?.inviteEmail) return;
    const cur = registerForm.getValues('email');
    if (cur && cur.trim() !== '') return;
    registerForm.setValue('email', invitePreview.inviteEmail);
  }, [invitePreview, invitePreviewStatus, registerForm]);

  const inviteStudentName =
    invitePreviewStatus === 'valid' && invitePreview
      ? formatManualInviteStudentName(invitePreview)
      : null;

  const loginPath = orgProfile
    ? buildOrgLoginPath(
        {
          slug: orgProfile.organization.slug,
          subdomain: orgProfile.organization.subdomain,
          subdomainEnabled: orgProfile.organization.subdomainEnabled,
        },
        returnUrl ?? dashboardReturnPath
      )
    : '/login';

  const handleRegisterSubmit = useCallback(
    async (data: UserFormData) => {
      if (!orgProfile) return;

      if (inviteStudentName && !manualInviteAccountFor) {
        toast.error('Please choose who this account is for before continuing.');
        return;
      }

      setRegisterSubmitting(true);
      try {
        if (orgProfile.modules.hasAcademy && academy?.id && orgProfile.academySlug) {
          const result = await submitAcademyRegister({
            data,
            academyId: academy.id,
            academySlug: orgProfile.academySlug,
            dashboardReturnPath,
            manualInviteToken,
            manualInviteAccountFor,
          });
          if (result.type === 'verification') {
            const orgFields = {
              slug: orgProfile.organization.slug,
              subdomain: orgProfile.organization.subdomain,
              subdomainEnabled: orgProfile.organization.subdomainEnabled,
            };
            navigate(
              buildOrgEmailVerificationPath(orgFields, {
                email: result.email,
                returnUrl: result.returnUrl,
                academySlug: result.academySlug,
              }),
              {
                state: result.verificationEmailFailed ? { verificationEmailSendFailed: true } : undefined,
              }
            );
            return;
          }
          if (result.type === 'login') {
            toast.error(result.message);
            navigate(`${loginPath}${loginPath.includes('?') ? '&' : '?'}email=${encodeURIComponent(result.email)}`);
            return;
          }
          if (result.type === 'error') {
            toast.error(result.message);
          }
          return;
        }

        const clubResult = await submitClubRegister({ data, returnPath: dashboardReturnPath });
        if (clubResult.type === 'success') {
          if (clubResult.verificationEmailFailed) {
            toast.error('Account created, but we could not send the verification email. You can resend it when you sign in.');
          } else {
            toast.success('Account created! Continue with membership — verify your email next time you sign in.');
          }
          navigate(clubResult.returnUrl);
          return;
        }
        if (clubResult.type === 'login') {
          toast.error(clubResult.message);
          navigate(`${loginPath}${loginPath.includes('?') ? '&' : '?'}email=${encodeURIComponent(clubResult.email)}`);
          return;
        }
        if (clubResult.type === 'error') {
          toast.error(clubResult.message);
        }
      } finally {
        setRegisterSubmitting(false);
      }
    },
    [
      orgProfile,
      academy,
      dashboardReturnPath,
      manualInviteToken,
      manualInviteAccountFor,
      inviteStudentName,
      loginPath,
      navigate,
    ]
  );

  if (loadingOrg || !orgSlug) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  if (orgError || !orgProfile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <p className="text-red-600 dark:text-red-400 mb-4">Organization not found</p>
        <Link to="/login" className="text-primary-600 dark:text-primary-400">
          Go to main login
        </Link>
      </div>
    );
  }

  if (academy && isStarterMemberAuthBlockedAcademy(academy)) {
    return <StarterMemberAuthNotFound academy={academy} academySlug={orgProfile.academySlug ?? orgSlug} />;
  }

  const org = orgProfile.organization;
  const supportsAcademySignup = orgProfile.modules.hasAcademy && Boolean(academy?.id);

  return (
    <AcademyBrandingProvider primaryColor={org.primaryColor ?? undefined} secondaryColor={org.secondaryColor ?? undefined}>
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-tennis-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            {org.logoUrl ? (
              <img src={org.logoUrl} alt={org.name} className="h-16 w-auto mx-auto mb-4 object-contain" />
            ) : null}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create your account</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Join {org.name}
              {supportsAcademySignup ? ' to book lessons' : ''}
              {orgProfile.modules.hasClub ? ' and manage membership' : ''}.
            </p>
          </div>

          {inviteStudentName ? (
            <div className="mb-6">
              <ManualInviteAccountTypeSelector
                studentName={inviteStudentName}
                value={manualInviteAccountFor}
                onChange={setManualInviteAccountFor}
              />
            </div>
          ) : null}

          <AcademyGuestRegistrationBlock
            registerSubmitting={registerSubmitting}
            form={registerForm}
            onEmailSubmit={handleRegisterSubmit}
            submitButtonLabel="Create account"
            defaultCountrySource={{
              country: org.country ?? academy?.country,
              region: (org.region ?? academy?.region) as Academy['region'],
            }}
          />
        </div>
      </div>
    </AcademyBrandingProvider>
  );
}
